import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type ReactNode, useMemo } from 'react';
import { z } from 'zod';
import { AppShell } from '../../components/AppShell.js';
import type { ScopeState } from '../../components/filters/types.js';
import { useScopeSelection } from '../../components/filters/useScopeSelection.js';
import { WorkflowsFilterBar } from '../../components/filters/WorkflowsFilterBar.js';
import { AutomationsTab } from '../../components/workflows/automations/AutomationsTab.js';
import { RunsTab } from '../../components/workflows/runs/RunsTab.js';
import { runSearch, withoutTab } from '../../components/workflows/runs/runSearch.js';
import { WorkflowsCatalog } from '../../components/workflows/WorkflowsCatalog.js';
import { validateFilterSearch } from '../../lib/filter-search.js';
import {
  mergeScopeIntoSearch,
  scopeFromSearch,
  scopeSearchShape,
  searchHasScope,
} from '../../lib/scope-search.js';
import { useAuthenticatedSession } from '../../lib/useAuthenticatedSession.js';
import { useShellAccount } from '../../lib/useShellAccount.js';

const WORKFLOWS_TABS = ['catalog', 'automations', 'runs'] as const;

type WorkflowsTab = (typeof WORKFLOWS_TABS)[number];

function WorkflowsPage(): ReactNode {
  const navigate = Route.useNavigate();
  const openWorkflow = useNavigate();
  const search = Route.useSearch();
  const { session, isPending, onSignOut } = useAuthenticatedSession();
  const account = useShellAccount(session?.user.email);
  const tab: WorkflowsTab = search.tab ?? 'catalog';
  const scopeSearch = useMemo(() => scopeFromSearch(search), [search]);
  const hasScope = searchHasScope(search);
  const setUrlScope = (next: ScopeState): void => {
    void navigate({
      replace: true,
      search: (previous) => mergeScopeIntoSearch(previous, next),
    });
  };
  const { scope, applyScope } = useScopeSelection({
    scope: scopeSearch,
    onScopeChange: setUrlScope,
    urlHasScope: hasScope,
  });

  if (isPending || session === null) {
    return (
      <Box
        sx={{
          display: 'flex',
          minHeight: '100vh',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <AppShell
      active="workflows"
      account={account}
      onLogout={() => void onSignOut()}
      topBar={<WorkflowsFilterBar bar="workflows" scope={scope} onScopeChange={applyScope} />}
      sx={{ mx: 34, py: 6 }}
    >
      <Tabs
        value={tab}
        onChange={(_event, next: WorkflowsTab) =>
          void navigate({ replace: true, search: (previous) => ({ ...previous, tab: next }) })
        }
        sx={{ mb: 3 }}
      >
        <Tab value="catalog" label="Catalog" />
        <Tab value="automations" label="Automations" />
        <Tab value="runs" label="Runs" />
      </Tabs>

      {tab === 'automations' ? (
        <AutomationsTab
          onOpenRun={(run) =>
            void openWorkflow({
              to: '/workflows/$workflowId',
              params: { workflowId: run.actionType },
              search: (previous) => runSearch(previous, run),
            })
          }
        />
      ) : tab === 'runs' ? (
        <RunsTab
          leadingKpi={scope.leadingKpi}
          onOpenRun={(run) =>
            void openWorkflow({
              to: '/workflows/$workflowId',
              params: { workflowId: run.actionType },
              search: (previous) => runSearch(previous, run),
            })
          }
        />
      ) : (
        <WorkflowsCatalog
          onOpenWorkflow={(workflowId, goal) =>
            void openWorkflow({
              to: '/workflows/$workflowId',
              params: { workflowId },
              search: (previous) => ({ ...withoutTab(previous), goal }),
            })
          }
        />
      )}
    </AppShell>
  );
}

export const Route = createFileRoute('/workflows/')({
  validateSearch: (search: Record<string, unknown>) => ({
    ...validateFilterSearch(search),
    ...z.object(scopeSearchShape).catch({}).parse(search),
    ...z
      .object({ tab: z.enum(WORKFLOWS_TABS).optional().catch(undefined) })
      .catch({})
      .parse(search),
  }),
  component: WorkflowsPage,
});
